// Planning a trip that can use a mode with stops of its own: the request, the
// ends it names, and the words for a plan that comes back empty. The screens
// that ask are the boards (src/use-board-trip.js); there is no Plan screen.
//
// THE SWITCHES ARE THE SHARED MODE FILTER (src/mode-filter.js): trains (V/Line
// with them, because a person choosing "trains" does not mean "except the one to
// Geelong") and each mode that declares a `planner` in src/modes.js are chips in
// the one setting every search in the app reads, and the last one on cannot be
// turned off. The query string and the label use the mode's `planner.key`, and a
// mode switched off is never used for a leg (`planSwitches`).
//
// ALL THE MODES, PER REQUEST. The server plans trains plus EVERY planner mode
// that is on (`server/street-plan.mjs`): a plan's ends are a station or a stop of
// any of them, each end brings its own mode (tram > train > bus), a tram can
// change to a bus at stops within a short walk with no train, and a station end
// also takes the stops beside it. A city with two such modes (Melbourne's trams
// and buses) has two endpoints that answer the same plan; the screens pick the
// one for the stop they are about. `plannerMode(city)` is only the first of a
// city's planner modes, kept for what asks for "the" one.
//

import { API_BASE } from './core/native.js';
import { askPatiently, busyError } from './busy.js';
import { everyAdapterMode, modeOfRide, modesOf } from './modes.js';

/** Every shown mode of a city that declares a planner, in registry order. */
export const plannerModes = (city) => modesOf(city).filter((m) => m.source === 'adapter' && m.planner);

/** The first mode a city plans with besides trains, or null. See the note above. */
export const plannerMode = (city) => plannerModes(city)[0] ?? null;

/** Every switch key a stored choice may hold: trains, then each planner mode's. */
export const planKeys = () => ['trains', ...everyAdapterMode().filter((m) => m.planner).map((m) => m.planner.key)];

/** The switches the server reads, by name: `trains`, then each planner mode's `planner.key`. `isOn(id)` is the shared filter's. */
export function planSwitches(city, isOn) {
  const out = { trains: isOn('train') };
  for (const m of plannerModes(city)) out[m.planner.key] = isOn(m.id);
  return out;
}

/** `tram:2077,2078` or `station:1071` — one end of a trip, as the server reads it. */
export function endParam(end) {
  if (!end) return '';
  if (end.kind === 'station') return `station:${Number(end.stopId)}`;
  return `${end.kind}:${(end.stopIds ?? [end.stopId]).map(Number).join(',')}`;
}

/** The plan, or a thrown error the screen can say. */
export async function fetchModePlan({ mode, from, to, modes, at = null, slower = false }) {
  const query = new URLSearchParams({ from: endParam(from), to: endParam(to) });
  for (const key of Object.keys(modes)) query.set(key, modes[key] ? '1' : '0');
  if (at) query.set('at', String(at));
  if (slower) query.set('slower', '1');
  const answer = await askPatiently(async () => {
    try {
      const res = await fetch(`${API_BASE}${mode.planner.api}?${query}`, { signal: AbortSignal.timeout(15_000) });
      const body = await res.json().catch(() => null);
      return { ok: res.ok, body, status: res.status };
    } catch {
      return { ok: false, body: null, status: 0 };
    }
  });
  if (answer.ok && Array.isArray(answer.body?.options)) return answer.body;
  throw answer.busy ? busyError(answer) : new Error('Couldn’t plan that just now. Pull down to try again.');
}

/**
 * The trip rows a screen draws, in order: a leg of a mode with stops of its own is its badge, a train leg
 * its line, a walk its minutes. Pure, so the screen and the suite agree on
 * what "67 direct, every 5 min" and "walk 9 › Pakenham" are made of.
 */
const modeKinds = () => new Set(everyAdapterMode().map((m) => m.id));

export function chainOf(option) {
  const legs = option?.legs ?? [];
  const moving = legs.filter((l) => l.kind !== 'walk');
  return legs.map((leg, i) => {
    if (leg.kind === 'walk') {
      // A walk between two vehicles is the change, and the change is already
      // drawn as the arrow; only a walk at either end is worth a word.
      if (i > 0 && i < legs.length - 1) return null;
      return { kind: 'walk', minutes: leg.minutes };
    }
    // A leg of a mode with stops of its own names the mode as its kind.
    if (modeOfRide(leg)) {
      return {
        kind: leg.kind,
        routeId: leg.routeId,
        direct: moving.length === 1,
        every: leg.every ?? null,
        // The tram that is changed onto, and where from. Present only then.
        ...(leg.change ? { change: { atStopId: leg.change.atStopId, minutes: leg.change.minutes } } : {}),
      };
    }
    return {
      kind: 'train',
      routeId: leg.routeId,
      name: leg.routeName ?? '',
      delayMinutes: Number(leg.delayMinutes) > 0 ? Number(leg.delayMinutes) : null,
      bus: leg.kind === 'bus',
    };
  }).filter(Boolean);
}

/**
 * What to say when a plan comes back with nothing.
 *
 * Between two stops of the mode, with no route that runs straight from one to
 * the other, the planner has already tried ONE change of vehicle; nothing
 * coming back means no connection in the next few hours, and two changes are
 * the one thing it does not do. Said in the mode's words, and honestly about
 * that limit. Otherwise the answer is about the time, or about trains.
 */
export function emptyPlanCopy({ mode, trains = true, modeToMode = false, direct = false, cross = false } = {}) {
  const many = mode?.labels?.many ?? 'trams';
  const one = mode?.labels?.one ?? 'tram';
  // A tram stop to a bus stop: one change between them is planned, two are not.
  if (modeToMode && cross) {
    const base = 'No connection with one change in the next few hours. Trips that change twice aren\u2019t planned.';
    return trains ? base : `${base} Turn Trains on to try one.`;
  }
  if (modeToMode && !direct) {
    const base = `No ${one} connection with one change in the next few hours. Trips that change ${many} twice aren't planned.`;
    return trains ? base : `${base} Turn Trains on to try one.`;
  }
  if (!trains) return `No ${one} goes there from here without a train. Turn Trains on to plan one.`;
  return 'Nothing gets there from here in the next few hours.';
}

/** The inverse of `endParam`, for a query string. Null for anything else. */
export function parseEndParam(text) {
  const m = /^([a-z][a-z0-9-]*):([\d,]+)$/.exec(String(text ?? '').trim());
  if (!m || (m[1] !== 'station' && !modeKinds().has(m[1]))) return null;
  const ids = m[2].split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0);
  if (!ids.length) return null;
  return m[1] === 'station' ? { kind: 'station', stopId: ids[0] } : { kind: m[1], stopIds: ids };
}
